import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Trophy, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIMENSIONS = [
  { key: "attention", label: "Attention", weight: 25 },
  { key: "cognitive_clarity", label: "Cognitive Clarity", weight: 20 },
  { key: "emotional_connection", label: "Emotional Connection", weight: 20 },
  { key: "brand_visibility", label: "Brand Visibility", weight: 15 },
  { key: "memorability", label: "Memorability", weight: 10 },
  { key: "cta_effectiveness", label: "CTA Effectiveness", weight: 10 },
];

export default function ComparePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ids = searchParams.get("ids")?.split(",").filter(Boolean) || [];
    if (ids.length < 2) {
      setError("Select at least 2 analyses to compare");
      setLoading(false);
      return;
    }
    const fetchCompare = async () => {
      try {
        const { data } = await axios.post(`${API}/analyses/compare`, { analysis_ids: ids }, { withCredentials: true });
        setAnalyses(data.analyses);
      } catch (err) {
        setError("Failed to load comparison");
      }
      setLoading(false);
    };
    fetchCompare();
  }, [searchParams]);

  if (loading) return (
    <div className="max-w-6xl space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-6 w-32 bg-slate-200 rounded animate-pulse" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        {[1, 2].map(i => <div key={i} className="h-96 bg-white rounded-2xl animate-pulse" />)}
      </div>
    </div>
  );

  if (error) return (
    <div className="max-w-6xl space-y-6">
      <button onClick={() => navigate("/analyses")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-orange-600">
        <ArrowLeft size={14} /> Back to analyses
      </button>
      <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );

  // Find winner for each dimension
  const getWinner = (dimKey) => {
    let best = -1, winnerIdx = -1;
    analyses.forEach((a, i) => {
      const score = a.results?.neuro_breakdown?.[dimKey]?.score || 0;
      if (score > best) { best = score; winnerIdx = i; }
    });
    return winnerIdx;
  };

  const overallWinnerIdx = analyses.reduce((bestIdx, a, i, arr) =>
    (a.results?.neuro_score || 0) > (arr[bestIdx]?.results?.neuro_score || 0) ? i : bestIdx, 0);

  const scoreColor = (s) => s >= 80 ? "text-emerald-600" : s >= 50 ? "text-amber-600" : "text-red-500";
  const scoreBg = (s) => s >= 80 ? "bg-emerald-50" : s >= 50 ? "bg-amber-50" : "bg-red-50";
  const ringColor = (s) => s >= 80 ? "#10B981" : s >= 50 ? "#F59E0B" : "#EF4444";

  return (
    <div className="max-w-6xl space-y-6">
      <button onClick={() => navigate("/analyses")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-orange-600 transition-colors">
        <ArrowLeft size={14} /> Back to analyses
      </button>

      <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
        Compare Analyses
      </h1>

      {/* Ad Cards - Side by Side */}
      <div className={`grid gap-4 ${analyses.length === 2 ? "grid-cols-2" : analyses.length === 3 ? "grid-cols-3" : "grid-cols-4"}`}>
        {analyses.map((a, i) => {
          const r = a.results || {};
          const isWinner = i === overallWinnerIdx;
          return (
            <div key={a.id} className={`bg-white rounded-2xl border-2 p-5 relative ${isWinner ? "border-emerald-400 shadow-lg shadow-emerald-50" : "border-slate-200"}`}>
              {isWinner && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1">
                  <Trophy size={10} /> WINNER
                </div>
              )}
              {/* Image */}
              {a.image_base64 && (
                <img src={a.image_base64} alt={a.ad_name} className="w-full h-32 object-cover rounded-xl border border-slate-100 mb-3" />
              )}
              {/* Name & Meta */}
              <h3 className="text-sm font-bold text-slate-800 truncate">{a.ad_name}</h3>
              <div className="flex flex-wrap gap-1 mt-1 mb-3">
                <Badge variant="secondary" className="text-[9px]">{a.category}</Badge>
                <Badge variant="secondary" className="text-[9px]">{a.platform}</Badge>
              </div>
              {/* Score Ring */}
              <div className="text-center">
                <div className="relative w-24 h-24 mx-auto">
                  <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="#E2E8F0" strokeWidth="8" />
                    <circle cx="60" cy="60" r="52" fill="none" stroke={ringColor(r.neuro_score || 0)} strokeWidth="8"
                      strokeDasharray={`${((r.neuro_score || 0) / 100) * 326.7} 326.7`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-2xl font-bold ${scoreColor(r.neuro_score || 0)}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>{r.neuro_score || 0}</span>
                    <span className="text-[9px] text-slate-400">/ 100</span>
                  </div>
                </div>
                <Badge className={`mt-2 ${scoreBg(r.neuro_score || 0)} ${scoreColor(r.neuro_score || 0)} border-0 text-[10px]`}>
                  {r.score_label || "—"}
                </Badge>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dimension Comparison Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>Score Breakdown Comparison</h2>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Dimension</th>
              <th className="text-center text-xs font-medium text-slate-500 px-3 py-3">Weight</th>
              {analyses.map((a, i) => (
                <th key={i} className="text-center text-xs font-medium text-slate-500 px-3 py-3">{a.ad_name?.substring(0, 15)}{a.ad_name?.length > 15 ? "..." : ""}</th>
              ))}
              <th className="text-center text-xs font-medium text-slate-500 px-3 py-3">Winner</th>
            </tr>
          </thead>
          <tbody>
            {/* Overall row */}
            <tr className="border-b border-slate-200 bg-indigo-50">
              <td className="px-5 py-3 text-sm font-bold text-indigo-700">Overall Neuro Score</td>
              <td className="text-center text-xs text-slate-500">100%</td>
              {analyses.map((a, i) => (
                <td key={i} className="text-center py-3">
                  <span className={`text-lg font-bold ${scoreColor(a.results?.neuro_score || 0)}`} style={{ fontFamily: "JetBrains Mono" }}>
                    {a.results?.neuro_score || 0}
                  </span>
                </td>
              ))}
              <td className="text-center py-3">
                <span className="text-xs font-bold text-emerald-600">{analyses[overallWinnerIdx]?.ad_name?.substring(0, 12)}</span>
              </td>
            </tr>
            {/* Dimension rows */}
            {DIMENSIONS.map(dim => {
              const winnerIdx = getWinner(dim.key);
              return (
                <tr key={dim.key} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-3 text-sm text-slate-700">{dim.label}</td>
                  <td className="text-center text-xs text-slate-400">{dim.weight}%</td>
                  {analyses.map((a, i) => {
                    const score = a.results?.neuro_breakdown?.[dim.key]?.score || 0;
                    const isWin = i === winnerIdx;
                    return (
                      <td key={i} className="text-center py-3">
                        <span className={`text-sm font-bold ${isWin ? "text-emerald-600" : "text-slate-600"}`} style={{ fontFamily: "JetBrains Mono" }}>
                          {score}
                        </span>
                        {isWin && <Trophy size={10} className="inline ml-1 text-emerald-500" />}
                      </td>
                    );
                  })}
                  <td className="text-center py-3">
                    <span className="text-[10px] text-emerald-600 font-medium">{analyses[winnerIdx]?.ad_name?.substring(0, 12)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Engagement Comparison */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h2 className="text-base font-bold mb-4" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>Engagement Comparison</h2>
        <div className={`grid gap-4 ${analyses.length === 2 ? "grid-cols-2" : analyses.length === 3 ? "grid-cols-3" : "grid-cols-4"}`}>
          {analyses.map((a, i) => {
            const eng = a.results?.engagement || {};
            return (
              <div key={i} className="border border-slate-100 rounded-xl p-4 space-y-2">
                <p className="text-xs font-bold text-slate-700 truncate">{a.ad_name}</p>
                <div className="flex justify-between text-xs"><span className="text-slate-500">Level</span><span className="font-semibold text-slate-800">{eng.level || "—"}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">Percentile</span><span className="font-semibold text-slate-800">{eng.percentile || "—"}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">Platform Fit</span><span className="font-semibold text-slate-800">{eng.platform_fit_score || 0}/100</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">Predicted CTR</span><span className="font-semibold text-slate-800">{eng.predicted_ctr || "—"}</span></div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-2xl border border-indigo-100 p-6">
        <h2 className="text-base font-bold mb-2" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>Summary</h2>
        <p className="text-sm text-slate-600">
          <strong className="text-indigo-700">{analyses[overallWinnerIdx]?.ad_name}</strong> is the overall winner with a Neuro Score of{" "}
          <strong>{analyses[overallWinnerIdx]?.results?.neuro_score}</strong>.
          {analyses.length === 2 && (
            <> It outperforms <strong>{analyses[1 - overallWinnerIdx]?.ad_name}</strong> by{" "}
            <strong>{Math.abs((analyses[0]?.results?.neuro_score || 0) - (analyses[1]?.results?.neuro_score || 0))} points</strong>.
            {(() => {
              const wins = DIMENSIONS.map(d => ({ ...d, winner: getWinner(d.key) }));
              const ad0Wins = wins.filter(w => w.winner === 0).map(w => w.label);
              const ad1Wins = wins.filter(w => w.winner === 1).map(w => w.label);
              const loserIdx = 1 - overallWinnerIdx;
              const loserWins = loserIdx === 0 ? ad0Wins : ad1Wins;
              if (loserWins.length > 0) {
                return <> However, <strong>{analyses[loserIdx]?.ad_name}</strong> scores higher in {loserWins.join(", ")}.</>;
              }
              return null;
            })()}
            </>
          )}
        </p>
      </div>
    </div>
  );
}
